import type { FocusEventFrame } from "@mind/contracts/browser";

export const FOCUS_DELTA_BATCH_MS = 40;
export const FOCUS_STREAM_TEXT_LIMIT = 1_048_576;
export const FOCUS_SOURCE_LIMIT = 100;
export const FOCUS_EVENT_LOOKBACK = 1_000n;

export type OrderedEventSelection = {
  readonly accepted: readonly FocusEventFrame[];
  readonly lastSequence: string;
};

export function acceptOrderedEvents(
  expectedRunId: string,
  lastSequence: string,
  frames: readonly FocusEventFrame[],
): OrderedEventSelection {
  let cursor = BigInt(lastSequence);
  const accepted: FocusEventFrame[] = [];
  for (const frame of frames) {
    if (frame.runId !== expectedRunId) continue;
    const sequence = BigInt(frame.eventSequence);
    if (sequence <= cursor) continue;
    accepted.push(frame);
    cursor = sequence;
  }
  return { accepted, lastSequence: cursor.toString(10) };
}

export function boundedEventCursor(authoritativeSequence: string): string {
  const sequence = BigInt(authoritativeSequence);
  return sequence > FOCUS_EVENT_LOOKBACK ? (sequence - FOCUS_EVENT_LOOKBACK).toString(10) : "0";
}

type TimerHandle = number;
type ScheduleTimer = (callback: () => void, delayMs: number) => TimerHandle;
type CancelTimer = (timer: TimerHandle) => void;

export class FocusDeltaBatcher {
  readonly #flush: (text: string) => void;
  readonly #schedule: ScheduleTimer;
  readonly #cancel: CancelTimer;
  readonly #windowMs: number;
  readonly #limit: number;
  #timer: TimerHandle | null = null;
  #pending = "";
  #acceptedCharacters = 0;

  constructor(
    flush: (text: string) => void,
    options: {
      readonly windowMs?: number;
      readonly characterLimit?: number;
      readonly schedule?: ScheduleTimer;
      readonly cancel?: CancelTimer;
    } = {},
  ) {
    this.#flush = flush;
    this.#windowMs = options.windowMs ?? FOCUS_DELTA_BATCH_MS;
    this.#limit = options.characterLimit ?? FOCUS_STREAM_TEXT_LIMIT;
    this.#schedule = options.schedule ?? ((callback, delayMs) => window.setTimeout(callback, delayMs));
    this.#cancel = options.cancel ?? ((timer) => window.clearTimeout(timer));
  }

  push(text: string): void {
    const remaining = this.#limit - this.#acceptedCharacters;
    if (remaining <= 0 || text.length === 0) return;
    const accepted = text.slice(0, remaining);
    this.#pending += accepted;
    this.#acceptedCharacters += accepted.length;
    if (this.#timer !== null) return;
    this.#timer = this.#schedule(() => {
      this.#timer = null;
      this.flushNow();
    }, this.#windowMs);
  }

  flushNow(): void {
    if (this.#pending.length === 0) return;
    const text = this.#pending;
    this.#pending = "";
    this.#flush(text);
  }

  dispose(): void {
    if (this.#timer !== null) this.#cancel(this.#timer);
    this.#timer = null;
    this.#pending = "";
  }
}
